import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  WORKFLOW_STEPS,
  getDeepDDTargets,
  hubSpotRequest,
  runWorkflowStep,
  saveDeepDDTargets,
} from "./deepDDApi";
import "./DeepDD.scss";

const defaultProperties = {
  property_report_url: "dociify_report_url",
  property_payload_json: "dociify_payload_json",
  property_investigation_id: "dociify_investigation_id",
  property_last_run_at: "dociify_last_run_at",
  property_risk_level: "dociify_risk_level",
  property_risk_score: "dociify_risk_score",
  property_summary: "dociify_summary",
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const runFailureReason = (run) => {
  if (run.error_message) return run.error_message;
  const failedOutcome = (run.details?.outcomes || []).find((outcome) => outcome.status === "failed");
  if (!failedOutcome) return "-";
  return failedOutcome.error || "Record failed without a detailed error.";
};

const parseRunDate = (value) => {
  const date = new Date(String(value || "").replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
};

const dayKey = (date) => date.toISOString().slice(0, 10);
const hourKey = (date) => `${dayKey(date)}-${String(date.getHours()).padStart(2, "0")}`;

const frequencyLabels = {
  immediate: "Run immediately",
  every_5_minutes: "Every 5 minutes",
  every_30_minutes: "Every 30 minutes",
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
  manual: "Manual only",
};

const CONNECTOR_STORAGE_KEY = "deepdd_connector_profiles";
const RUN_LOG_PAGE_SIZE = 10;
const RUN_CHART_RANGES = [1, 15, 30];
const settingsTabs = [
  { key: "integration", label: "Integration" },
  { key: "workflow", label: "Workflow" },
  { key: "automation", label: "Automation" },
  { key: "notifications", label: "Notifications" },
  { key: "logs", label: "Run Log" },
];

const workflowParameterDefinitions = {
  initialize: {
    summary: "Subject intake, JSON validation, and investigation initialization.",
    fields: [
      { key: "require_name", label: "Require subject name", type: "checkbox", defaultValue: true },
      { key: "require_company_or_email", label: "Require company or email", type: "checkbox", defaultValue: true },
      { key: "normalize_domains", label: "Normalize website domains", type: "checkbox", defaultValue: true },
      { key: "max_notes_chars", label: "Max notes characters", type: "number", defaultValue: 4000, min: 500, max: 20000 },
    ],
  },
  discovery: {
    summary: "Public web discovery and initial source collection.",
    fields: [
      { key: "result_limit", label: "Result limit", type: "number", defaultValue: 10, min: 1, max: 50 },
      { key: "include_company_aliases", label: "Include company aliases", type: "checkbox", defaultValue: true },
      { key: "include_person_search", label: "Include person search", type: "checkbox", defaultValue: true },
      { key: "preferred_sources", label: "Preferred sources", type: "text", defaultValue: "company website, LinkedIn, news, registry" },
    ],
  },
  crawl: {
    summary: "Website crawl depth, timeout, and allowed pages.",
    fields: [
      { key: "page_limit", label: "Page limit", type: "number", defaultValue: 20, min: 1, max: 200 },
      { key: "depth", label: "Crawl depth", type: "number", defaultValue: 2, min: 1, max: 5 },
      { key: "timeout_seconds", label: "Timeout seconds", type: "number", defaultValue: 30, min: 5, max: 180 },
      { key: "include_pdf_links", label: "Include PDF links", type: "checkbox", defaultValue: true },
    ],
  },
  extract: {
    summary: "Entity extraction and evidence parsing settings.",
    fields: [
      { key: "extract_people", label: "Extract people", type: "checkbox", defaultValue: true },
      { key: "extract_addresses", label: "Extract addresses", type: "checkbox", defaultValue: true },
      { key: "extract_identifiers", label: "Extract identifiers", type: "checkbox", defaultValue: true },
      { key: "confidence_threshold", label: "Confidence threshold", type: "number", defaultValue: 70, min: 0, max: 100 },
    ],
  },
  leadership: {
    summary: "Leadership, beneficial ownership, and registration enrichment.",
    fields: [
      { key: "search_business_registries", label: "Search business registries", type: "checkbox", defaultValue: true },
      { key: "include_beneficial_owners", label: "Include beneficial owners", type: "checkbox", defaultValue: true },
      { key: "max_people", label: "Max people", type: "number", defaultValue: 12, min: 1, max: 50 },
      { key: "add_people_to_ofac", label: "Add discovered people to OFAC", type: "checkbox", defaultValue: true },
    ],
  },
  social: {
    summary: "Social footprint and profile matching.",
    fields: [
      { key: "platforms", label: "Platforms", type: "text", defaultValue: "LinkedIn, X, Facebook, Instagram, YouTube, GitHub" },
      { key: "profile_limit", label: "Profile limit", type: "number", defaultValue: 15, min: 1, max: 100 },
      { key: "include_company_profiles", label: "Include company profiles", type: "checkbox", defaultValue: true },
      { key: "match_threshold", label: "Match threshold", type: "number", defaultValue: 70, min: 0, max: 100 },
    ],
  },
  military: {
    summary: "Military affiliation and restricted-sector signal checks.",
    fields: [
      { key: "include_defense_sources", label: "Include defense sources", type: "checkbox", defaultValue: true },
      { key: "include_procurement_mentions", label: "Include procurement mentions", type: "checkbox", defaultValue: true },
      { key: "country_focus", label: "Country focus", type: "text", defaultValue: "US, Canada, UK, EU, Ukraine, Russia, China" },
      { key: "result_limit", label: "Result limit", type: "number", defaultValue: 10, min: 1, max: 50 },
    ],
  },
  darkweb: {
    summary: "Dark web, breach, credential, and leak indicators.",
    fields: [
      { key: "include_breach_sources", label: "Include breach sources", type: "checkbox", defaultValue: true },
      { key: "include_tor_sources", label: "Include Tor sources", type: "checkbox", defaultValue: false },
      { key: "keyword_limit", label: "Keyword limit", type: "number", defaultValue: 12, min: 1, max: 50 },
      { key: "severity_threshold", label: "Severity threshold", type: "select", defaultValue: "medium", options: ["low", "medium", "high"] },
    ],
  },
  domain: {
    summary: "Domain age, DNS, website health, and infrastructure checks.",
    fields: [
      { key: "include_whois", label: "Include WHOIS", type: "checkbox", defaultValue: true },
      { key: "include_dns", label: "Include DNS", type: "checkbox", defaultValue: true },
      { key: "include_ssl", label: "Include SSL", type: "checkbox", defaultValue: true },
      { key: "risk_age_years", label: "Young domain risk age", type: "number", defaultValue: 2, min: 0, max: 20 },
    ],
  },
  ofac: {
    summary: "Sanctions screening scope and matching controls.",
    fields: [
      { key: "include_subject", label: "Screen subject", type: "checkbox", defaultValue: true },
      { key: "include_company", label: "Screen company", type: "checkbox", defaultValue: true },
      { key: "include_related_people", label: "Screen related people", type: "checkbox", defaultValue: true },
      { key: "fuzzy_threshold", label: "Fuzzy match threshold", type: "number", defaultValue: 85, min: 50, max: 100 },
    ],
  },
  ai: {
    summary: "AI review depth, confidence, and adverse-media summary treatment.",
    fields: [
      { key: "review_depth", label: "Review depth", type: "select", defaultValue: "standard", options: ["light", "standard", "deep"] },
      { key: "include_reputation_summary", label: "Include reputation summary", type: "checkbox", defaultValue: true },
      { key: "include_standards_mapping", label: "Include standards mapping", type: "checkbox", defaultValue: true },
      { key: "min_confidence", label: "Minimum confidence", type: "number", defaultValue: 70, min: 0, max: 100 },
    ],
  },
  risk: {
    summary: "Risk scoring weights and escalation thresholds.",
    fields: [
      { key: "sanctions_weight", label: "Sanctions weight", type: "number", defaultValue: 35, min: 0, max: 100 },
      { key: "reputation_weight", label: "Reputation weight", type: "number", defaultValue: 25, min: 0, max: 100 },
      { key: "domain_weight", label: "Domain weight", type: "number", defaultValue: 15, min: 0, max: 100 },
      { key: "high_risk_threshold", label: "High risk threshold", type: "number", defaultValue: 70, min: 0, max: 100 },
    ],
  },
  report: {
    summary: "PDF report composition, branding, and evidence display.",
    fields: [
      { key: "include_logo", label: "Include 4PAY logo", type: "checkbox", defaultValue: true },
      { key: "include_kpi_charts", label: "Include KPI charts", type: "checkbox", defaultValue: true },
      { key: "include_raw_evidence", label: "Include raw evidence appendix", type: "checkbox", defaultValue: false },
      { key: "report_tone", label: "Report tone", type: "select", defaultValue: "executive", options: ["executive", "analyst", "technical"] },
    ],
  },
};

const createWorkflowParameters = (overrides = {}) =>
  WORKFLOW_STEPS.reduce((result, step) => {
    const definition = workflowParameterDefinitions[step.key] || { fields: [] };
    const savedStep = overrides[step.key] || {};
    result[step.key] = definition.fields.reduce((stepResult, field) => {
      stepResult[field.key] = savedStep[field.key] ?? field.defaultValue;
      return stepResult;
    }, {});
    return result;
  }, {});

const createEtlRule = (overrides = {}) => ({
  id: overrides.id || `rule-${Date.now()}`,
  name: overrides.name || "New ETL rule",
  rule_type: overrides.rule_type || "query",
  account_rule: overrides.account_rule || "new_accounts",
  query: overrides.query || "",
  flag_property: overrides.flag_property || "requires_4pay_enrichment",
  flag_trigger_value: overrides.flag_trigger_value || "Yes",
  flag_complete_value: overrides.flag_complete_value || "Done",
  enabled: overrides.enabled ?? true,
});

const normalizeEtlRule = (rule) => {
  const normalized = createEtlRule(rule);
  const query = normalized.query.trim().toLowerCase();
  if (
    normalized.rule_type === "query"
    && normalized.account_rule === "property_flag"
    && query === "requires 4pay enrichment yes"
  ) {
    return createEtlRule({
      ...normalized,
      rule_type: "property_flag",
      query: "",
      flag_property: "requires_4pay_enrichment",
      flag_trigger_value: "Yes",
      flag_complete_value: normalized.flag_complete_value || "Done",
    });
  }
  return normalized;
};

const createSchedule = (overrides = {}) => ({
  enabled: false,
  frequency: "every_30_minutes",
  scope: "all",
  processing_rule: "new_accounts",
  query: "",
  limit: 25,
  daily_notification_enabled: true,
  daily_notification_threshold: 20,
  daily_limit: 30,
  daily_notification_recipients: "",
  daily_timezone: "America/Toronto",
  ...overrides,
  daily_notification_recipients: Array.isArray(overrides.daily_notification_recipients)
    ? overrides.daily_notification_recipients.join(", ")
    : (overrides.daily_notification_recipients || ""),
});

const createConnectorProfile = (overrides = {}) => ({
  id: overrides.id || `hubspot-${Date.now()}`,
  provider: "hubspot",
  name: overrides.name || overrides.label || "HubSpot CRM",
  connection: {
    label: overrides.connection?.label || overrides.label || "HubSpot CRM",
    base_url: overrides.connection?.base_url || overrides.base_url || "https://api.hubapi.com",
    access_token: overrides.connection?.access_token || overrides.access_token || "",
    has_token: overrides.connection?.has_token || overrides.has_token || false,
    last_connected_at: overrides.connection?.last_connected_at || overrides.last_connected_at || null,
  },
  schedule: createSchedule(overrides.schedule || {}),
  runConfig: {
    processing_rule: "new_accounts",
    query: "",
    limit: 25,
    ...(overrides.runConfig || {}),
  },
  etlRules: Array.isArray(overrides.etlRules)
    ? overrides.etlRules.map((rule) => normalizeEtlRule(rule))
    : [],
  flagTarget: {
    property: "requires_4pay_enrichment",
    trigger_value: "Yes",
    complete_value: "Done",
    ...(overrides.flagTarget || {}),
  },
  properties: { ...defaultProperties, ...(overrides.properties || {}) },
  workflowParameters: createWorkflowParameters(overrides.workflowParameters || overrides.workflow_parameters || {}),
  writeBack: overrides.writeBack ?? true,
  lastScheduledRun: overrides.lastScheduledRun || null,
});

const readConnectorProfiles = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(CONNECTOR_STORAGE_KEY) || "{}");
    if (!Array.isArray(saved.profiles) || !saved.profiles.length) {
      const profile = createConnectorProfile({ id: "hubspot-primary" });
      return { profiles: [profile], activeId: profile.id, fromStorage: false };
    }
    return {
      profiles: saved.profiles.map((profile) => createConnectorProfile(profile)),
      activeId: saved.activeId || saved.profiles[0].id,
      fromStorage: true,
    };
  } catch (error) {
    const profile = createConnectorProfile({ id: "hubspot-primary" });
    return { profiles: [profile], activeId: profile.id, fromStorage: false };
  }
};

const persistConnectorProfiles = (profiles, activeId) => {
  window.localStorage.setItem(CONNECTOR_STORAGE_KEY, JSON.stringify({ profiles, activeId }));
};

const ruleLabels = {
  new_accounts: "New accounts only",
  force_all: "All accounts (forced)",
  property_flag: "Flagged HubSpot contacts",
};

function DeepDDSettings() {
  const navigate = useNavigate();
  const toastTimer = useRef(null);
  const initialConnectors = readConnectorProfiles();
  const shouldHydrateConnection = useRef(!initialConnectors.fromStorage);
  const initialProfile = initialConnectors.profiles.find((profile) => profile.id === initialConnectors.activeId)
    || initialConnectors.profiles[0];
  const [connectorProfiles, setConnectorProfiles] = useState(initialConnectors.profiles);
  const [activeConnectorId, setActiveConnectorId] = useState(initialProfile.id);
  const [connection, setConnection] = useState(initialProfile.connection);
  const [apiTargets, setApiTargets] = useState(getDeepDDTargets);
  const [schedule, setSchedule] = useState(initialProfile.schedule);
  const [runConfig, setRunConfig] = useState(initialProfile.runConfig);
  const [etlRules, setEtlRules] = useState(initialProfile.etlRules);
  const [newEtlRule, setNewEtlRule] = useState(createEtlRule({
    name: "",
    query: "",
    account_rule: initialProfile.runConfig.processing_rule,
  }));
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [flagTarget, setFlagTarget] = useState(initialProfile.flagTarget);
  const [properties, setProperties] = useState(initialProfile.properties);
  const [workflowParameters, setWorkflowParameters] = useState(initialProfile.workflowParameters);
  const [activeWorkflowTab, setActiveWorkflowTab] = useState(WORKFLOW_STEPS[0].key);
  const [activeSettingsTab, setActiveSettingsTab] = useState("integration");
  const [writeBack, setWriteBack] = useState(initialProfile.writeBack);
  const [logs, setLogs] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const [runChartRange, setRunChartRange] = useState(1);
  const [technicalOutput, setTechnicalOutput] = useState("Ready.");
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [lastScheduledRun, setLastScheduledRun] = useState(initialProfile.lastScheduledRun);

  const showToast = (message, type = "success") => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = window.setTimeout(() => setToast(null), 4500);
  };

  const apiConnection = () => ({
    base_url: connection.base_url,
    ...(connection.access_token ? { access_token: connection.access_token } : {}),
  });

  const snapshotProfile = (profileId = activeConnectorId) => createConnectorProfile({
    id: profileId,
    name: connection.label || "HubSpot CRM",
    connection,
    schedule,
    runConfig,
    etlRules,
    flagTarget,
    properties,
    workflowParameters,
    writeBack,
    lastScheduledRun,
  });

  const saveProfiles = (profiles, activeId = activeConnectorId) => {
    setConnectorProfiles(profiles);
    setActiveConnectorId(activeId);
    persistConnectorProfiles(profiles, activeId);
  };

  const updateActiveProfile = (updates = {}) => {
    const nextProfile = createConnectorProfile({ ...snapshotProfile(), ...updates });
    const nextProfiles = connectorProfiles.map((profile) => (
      profile.id === activeConnectorId ? nextProfile : profile
    ));
    saveProfiles(nextProfiles, activeConnectorId);
    return nextProfile;
  };

  const applyConnectorProfile = (profile) => {
    setConnection(profile.connection);
    setSchedule(profile.schedule);
    setRunConfig(profile.runConfig);
    setEtlRules(profile.etlRules);
    setNewEtlRule(createEtlRule({ name: "", query: "", account_rule: profile.runConfig.processing_rule }));
    setEditingRuleId(null);
    setFlagTarget(profile.flagTarget);
    setProperties(profile.properties);
    setWorkflowParameters(profile.workflowParameters);
    setActiveWorkflowTab(WORKFLOW_STEPS[0].key);
    setWriteBack(profile.writeBack);
    setLastScheduledRun(profile.lastScheduledRun);
    setTechnicalOutput(`Active connector: ${profile.name}`);
  };

  const selectConnector = (profileId) => {
    const currentSnapshot = snapshotProfile();
    const targetProfile = connectorProfiles.find((profile) => profile.id === profileId);
    if (!targetProfile) return;
    const nextProfiles = connectorProfiles.map((profile) => (
      profile.id === activeConnectorId ? currentSnapshot : profile
    ));
    saveProfiles(nextProfiles, profileId);
    applyConnectorProfile(targetProfile);
    showToast(`${targetProfile.name} selected.`, "info");
  };

  const addConnector = () => {
    const currentSnapshot = snapshotProfile();
    const nextProfile = createConnectorProfile({
      id: `hubspot-${Date.now()}`,
      name: `HubSpot CRM ${connectorProfiles.length + 1}`,
      connection: {
        label: `HubSpot CRM ${connectorProfiles.length + 1}`,
        base_url: "https://api.hubapi.com",
        access_token: "",
      },
    });
    const nextProfiles = connectorProfiles
      .map((profile) => (profile.id === activeConnectorId ? currentSnapshot : profile))
      .concat(nextProfile);
    saveProfiles(nextProfiles, nextProfile.id);
    applyConnectorProfile(nextProfile);
    showToast("New HubSpot connector added.", "info");
  };

  const loadConnection = async () => {
    const data = await hubSpotRequest({ action: "connection_get" });
    if (!data.connection) return;
    const loadedConnection = {
      ...connection,
      label: data.connection.label || "HubSpot CRM",
      base_url: data.connection.base_url || "https://api.hubapi.com",
      access_token: "",
      has_token: data.connection.has_token,
      last_connected_at: data.connection.last_connected_at,
    };
    setConnection(loadedConnection);
    const savedSchedule = data.connection.settings?.schedule;
    if (savedSchedule) {
      const loadedSchedule = createSchedule({
        ...schedule,
        ...savedSchedule,
        enabled: Boolean(savedSchedule.enabled),
        frequency: savedSchedule.frequency === "manual"
          ? "every_30_minutes"
          : savedSchedule.frequency,
      });
      setSchedule(loadedSchedule);
    }
    const savedFlagTarget = data.connection.settings?.flag_target || savedSchedule?.flag_target;
    let loadedFlagTarget = flagTarget;
    if (savedFlagTarget) {
      loadedFlagTarget = { ...flagTarget, ...savedFlagTarget };
      setFlagTarget(loadedFlagTarget);
    }
    const loadedEtlRules = Array.isArray(data.connection.settings?.etl_rules)
      ? data.connection.settings.etl_rules.map((rule) => createEtlRule(rule))
      : etlRules;
    setEtlRules(loadedEtlRules);
    const loadedWorkflowParameters = createWorkflowParameters(data.connection.settings?.workflow_parameters || workflowParameters);
    setWorkflowParameters(loadedWorkflowParameters);
    const loadedLastRun = data.connection.settings?.last_scheduled_run || null;
    setLastScheduledRun(loadedLastRun);
    updateActiveProfile({
      name: loadedConnection.label || "HubSpot CRM",
      connection: loadedConnection,
      schedule: savedSchedule
        ? createSchedule({
          ...schedule,
          ...savedSchedule,
          enabled: Boolean(savedSchedule.enabled),
          frequency: savedSchedule.frequency === "manual"
            ? "every_30_minutes"
            : savedSchedule.frequency,
        })
        : schedule,
      flagTarget: loadedFlagTarget,
      etlRules: loadedEtlRules,
      workflowParameters: loadedWorkflowParameters,
      lastScheduledRun: loadedLastRun,
    });
  };

  const loadLogs = async () => {
    const data = await hubSpotRequest({ action: "run_log_list", limit: 100 });
    setLogs(data.runs || []);
    setLogPage(1);
  };

  useEffect(() => {
    const startupRequests = shouldHydrateConnection.current
      ? [loadConnection(), loadLogs()]
      : [loadLogs()];
    Promise.all(startupRequests).catch((error) => showToast(error.message, "error"));
    return () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
    // The initial load should only run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveConnection = async () => {
    setBusy(true);
    try {
      const selectedFlagTarget = flagTargetFor(schedule.processing_rule);
      const data = await hubSpotRequest({
        action: "connection_save",
        ...connection,
        settings: {
          schedule: { ...schedule, flag_target: selectedFlagTarget },
          flag_target: selectedFlagTarget,
          etl_rules: etlRules,
          workflow_parameters: workflowParameters,
        },
      });
      showToast(data.message || "HubSpot connection saved.");
      const savedProfile = updateActiveProfile({
        name: connection.label || "HubSpot CRM",
        connection: {
          ...connection,
          access_token: "",
          has_token: true,
          last_connected_at: new Date().toISOString(),
        },
      });
      setConnection(savedProfile.connection);
      await loadConnection();
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const saveApiTargets = () => {
    const normalized = saveDeepDDTargets(apiTargets);
    setApiTargets(normalized);
    showToast(`${normalized.environment === "production" ? "Production" : "Test"} API target is now active.`);
  };

  const removeConnection = async () => {
    if (!window.confirm("Remove the saved HubSpot connection?")) return;
    const data = await hubSpotRequest({ action: "connection_remove" });
    showToast(data.message || "HubSpot connection removed.");
    const nextProfiles = connectorProfiles.filter((profile) => profile.id !== activeConnectorId);
    const fallbackProfile = nextProfiles[0] || createConnectorProfile({ id: "hubspot-primary" });
    saveProfiles(nextProfiles.length ? nextProfiles : [fallbackProfile], fallbackProfile.id);
    applyConnectorProfile(fallbackProfile);
  };

  const updateWorkflowParameter = (stepKey, fieldKey, value) => {
    const nextParameters = {
      ...workflowParameters,
      [stepKey]: {
        ...(workflowParameters[stepKey] || {}),
        [fieldKey]: value,
      },
    };
    setWorkflowParameters(nextParameters);
    updateActiveProfile({ workflowParameters: nextParameters });
  };

  const resetWorkflowParameters = () => {
    if (!window.confirm("Reset workflow parameters to defaults for this connector?")) return;
    const defaults = createWorkflowParameters();
    setWorkflowParameters(defaults);
    updateActiveProfile({ workflowParameters: defaults });
    showToast("Workflow parameters reset.", "info");
  };

  const saveWorkflowParameters = async () => {
    setBusy(true);
    try {
      const data = await hubSpotRequest({
        action: "connection_save",
        ...connection,
        settings: {
          schedule: { ...schedule, flag_target: flagTargetFor(schedule.processing_rule) },
          flag_target: flagTarget,
          etl_rules: etlRules,
          workflow_parameters: workflowParameters,
        },
      });
      updateActiveProfile({ workflowParameters });
      showToast(data.message || "Workflow parameters saved.");
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const enabledRulesFor = (processingRule, ruleType = "query") => etlRules.filter((rule) => (
    rule.enabled && rule.rule_type === ruleType && rule.account_rule === processingRule
  ));

  const effectiveQuery = (baseQuery, processingRule) => [
    String(baseQuery || "").trim(),
    ...enabledRulesFor(processingRule, "query").map((rule) => rule.query.trim()).filter(Boolean),
  ].filter(Boolean).join(" ");

  const flagTargetFor = (processingRule) => {
    const rule = enabledRulesFor(processingRule, "property_flag")[0];
    if (!rule) return flagTarget;
    return {
      property: rule.flag_property || flagTarget.property,
      trigger_value: rule.flag_trigger_value || flagTarget.trigger_value,
      complete_value: rule.flag_complete_value || flagTarget.complete_value,
    };
  };

  const addEtlRule = () => {
    const name = newEtlRule.name.trim();
    const query = newEtlRule.query.trim();
    if (!name) {
      showToast("Add a rule name before saving it.", "error");
      return;
    }
    if (newEtlRule.rule_type === "query" && !query) {
      showToast("Add a query before saving a text query rule.", "error");
      return;
    }
    if (
      newEtlRule.rule_type === "property_flag"
      && (!newEtlRule.flag_property.trim() || !newEtlRule.flag_trigger_value.trim())
    ) {
      showToast("Add a HubSpot property and trigger value before saving a flag rule.", "error");
      return;
    }
    const normalizedRule = createEtlRule({ ...newEtlRule, id: editingRuleId || newEtlRule.id, name, query });
    const nextRules = editingRuleId
      ? etlRules.map((rule) => (rule.id === editingRuleId ? normalizedRule : rule))
      : etlRules.concat(normalizedRule);
    setEtlRules(nextRules);
    updateActiveProfile({ etlRules: nextRules });
    setNewEtlRule(createEtlRule({ name: "", query: "", account_rule: runConfig.processing_rule }));
    setEditingRuleId(null);
    showToast(editingRuleId ? "ETL rule updated." : "ETL rule added.", "success");
  };

  const updateEtlRule = (ruleId, updates) => {
    const nextRules = etlRules.map((rule) => (
      rule.id === ruleId ? normalizeEtlRule({ ...rule, ...updates }) : rule
    ));
    setEtlRules(nextRules);
    updateActiveProfile({ etlRules: nextRules });
  };

  const removeEtlRule = (ruleId) => {
    if (!window.confirm("Delete this ETL rule?")) return;
    const nextRules = etlRules.filter((rule) => rule.id !== ruleId);
    setEtlRules(nextRules);
    updateActiveProfile({ etlRules: nextRules });
    if (editingRuleId === ruleId) {
      setNewEtlRule(createEtlRule({ name: "", query: "", account_rule: runConfig.processing_rule }));
      setEditingRuleId(null);
    }
    showToast("ETL query rule removed.", "info");
  };

  const editEtlRule = (rule) => {
    setNewEtlRule(normalizeEtlRule(rule));
    setEditingRuleId(rule.id);
    showToast(`Editing ${rule.name}.`, "info");
  };

  const cancelRuleEdit = () => {
    setNewEtlRule(createEtlRule({ name: "", query: "", account_rule: runConfig.processing_rule }));
    setEditingRuleId(null);
  };

  const totalLogPages = Math.max(1, Math.ceil(logs.length / RUN_LOG_PAGE_SIZE));
  const safeLogPage = Math.min(logPage, totalLogPages);
  const logStartIndex = (safeLogPage - 1) * RUN_LOG_PAGE_SIZE;
  const pagedLogs = logs.slice(logStartIndex, logStartIndex + RUN_LOG_PAGE_SIZE);
  const isHourlyChart = runChartRange === 1;
  const chartEndDate = new Date();
  const chartStartDate = new Date(chartEndDate);
  if (isHourlyChart) {
    chartStartDate.setHours(0, 0, 0, 0);
    chartEndDate.setHours(23, 59, 59, 999);
  } else {
    chartEndDate.setHours(23, 59, 59, 999);
    chartStartDate.setDate(chartEndDate.getDate() - runChartRange + 1);
    chartStartDate.setHours(0, 0, 0, 0);
  }
  const chartBuckets = Array.from({ length: isHourlyChart ? 24 : runChartRange }, (_, index) => {
    const date = new Date(chartStartDate);
    if (isHourlyChart) {
      date.setHours(chartStartDate.getHours() + index);
    } else {
      date.setDate(chartStartDate.getDate() + index);
    }
    return {
      key: isHourlyChart ? hourKey(date) : dayKey(date),
      label: isHourlyChart
        ? date.toLocaleTimeString(undefined, { hour: "numeric" })
        : date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      runs: 0,
      selected: 0,
      successful: 0,
      failed: 0,
    };
  });
  const chartBucketMap = new Map(chartBuckets.map((bucket) => [bucket.key, bucket]));
  logs.forEach((run) => {
    const date = parseRunDate(run.started_at);
    if (!date || date < chartStartDate || date > chartEndDate) return;
    const bucket = chartBucketMap.get(isHourlyChart ? hourKey(date) : dayKey(date));
    if (!bucket) return;
    bucket.runs += 1;
    bucket.selected += Number(run.discovered_count || 0);
    bucket.successful += Number(run.success_count || 0);
    bucket.failed += Number(run.failed_count || 0);
  });
  const chartTotals = chartBuckets.reduce((totals, bucket) => ({
    runs: totals.runs + bucket.runs,
    selected: totals.selected + bucket.selected,
    successful: totals.successful + bucket.successful,
    failed: totals.failed + bucket.failed,
  }), { runs: 0, selected: 0, successful: 0, failed: 0 });
  const maxChartValue = Math.max(1, ...chartBuckets.map((bucket) => Math.max(bucket.selected, bucket.successful + bucket.failed)));

  const testConnection = async () => {
    setBusy(true);
    try {
      const data = await hubSpotRequest({ action: "test", ...apiConnection() });
      setTechnicalOutput(JSON.stringify(data, null, 2));
      showToast(data.message || "HubSpot connection is valid.");
    } catch (error) {
      showToast(error.message, "error");
      setTechnicalOutput(JSON.stringify({ ok: false, error: error.message }, null, 2));
    } finally {
      setBusy(false);
    }
  };

  const saveSchedule = async () => {
    setBusy(true);
    try {
      const selectedFlagTarget = flagTargetFor(schedule.processing_rule);
      const data = await hubSpotRequest({
        action: "schedule",
        ...schedule,
        query: effectiveQuery(schedule.query, schedule.processing_rule),
        processed_property: properties.property_investigation_id,
        flag_property: selectedFlagTarget.property,
        flag_trigger_value: selectedFlagTarget.trigger_value,
        flag_complete_value: selectedFlagTarget.complete_value,
      });
      updateActiveProfile({ schedule: { ...schedule, flag_target: selectedFlagTarget }, etlRules });
      showToast(
        schedule.enabled
          ? `${frequencyLabels[schedule.frequency] || schedule.frequency} schedule is active.`
          : "HubSpot schedule has been disabled."
      );
      setTechnicalOutput(JSON.stringify(data, null, 2));
      setSchedule((current) => ({ ...current, updated_at: new Date().toISOString() }));
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const notificationSettingsPayload = () => ({
    daily_notification_enabled: schedule.daily_notification_enabled,
    daily_notification_threshold: schedule.daily_notification_threshold,
    daily_limit: schedule.daily_limit,
    daily_notification_recipients: schedule.daily_notification_recipients,
    daily_timezone: schedule.daily_timezone,
  });

  const saveNotificationSettings = async () => {
    setBusy(true);
    try {
      const data = await hubSpotRequest({
        action: "notification_settings_save",
        ...notificationSettingsPayload(),
      });
      const updatedSchedule = { ...schedule, updated_at: new Date().toISOString() };
      setSchedule(updatedSchedule);
      updateActiveProfile({ schedule: updatedSchedule });
      setTechnicalOutput(JSON.stringify(data, null, 2));
      showToast(data.message || "Notification settings saved.");
    } catch (error) {
      setTechnicalOutput(JSON.stringify({ ok: false, error: error.message }, null, 2));
      showToast(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const sendTestNotification = async () => {
    setBusy(true);
    try {
      const data = await hubSpotRequest({
        action: "notification_test",
        ...notificationSettingsPayload(),
      });
      setTechnicalOutput(JSON.stringify(data, null, 2));
      showToast(data.message || "Test notification sent.");
    } catch (error) {
      setTechnicalOutput(JSON.stringify({ ok: false, error: error.message }, null, 2));
      showToast(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const previewAccounts = async () => {
    setBusy(true);
    try {
      const selectedFlagTarget = flagTargetFor(runConfig.processing_rule);
      const requestedFilter = {
        processing_rule: runConfig.processing_rule,
        query: effectiveQuery(runConfig.query, runConfig.processing_rule),
        flag_target: runConfig.processing_rule === "property_flag" ? selectedFlagTarget : null,
        processed_property: runConfig.processing_rule === "new_accounts" ? properties.property_investigation_id : null,
      };
      const data = await hubSpotRequest({
        action: "run",
        ...apiConnection(),
        scope: "all",
        query: requestedFilter.query,
        limit: runConfig.limit,
        processing_rule: runConfig.processing_rule,
        processed_property: properties.property_investigation_id,
        flag_property: selectedFlagTarget.property,
        flag_trigger_value: selectedFlagTarget.trigger_value,
        flag_complete_value: selectedFlagTarget.complete_value,
      });
      const appliedRule = data.processing_rule || data.filter_applied?.mode;
      const mismatch = appliedRule && appliedRule !== requestedFilter.processing_rule;
      setTechnicalOutput(JSON.stringify({
        requested_filter: requestedFilter,
        backend_filter_applied: data.filter_applied || {
          processing_rule: data.processing_rule,
          unprocessed_only: data.unprocessed_only,
          processed_property: data.processed_property,
          flag_target: data.flag_target || null,
        },
        warning: mismatch
          ? `Requested ${requestedFilter.processing_rule}, but backend returned ${appliedRule}. Refresh the Portal and confirm the deployed DeepDD API has the latest hubspot_api.php.`
          : null,
        response: data,
      }, null, 2));
      if (mismatch) {
        showToast(`Backend applied ${appliedRule}, not ${requestedFilter.processing_rule}.`, "error");
      } else if (runConfig.processing_rule === "property_flag" && !data.records?.[0]?.source?.flag_match) {
        showToast("Flag preview returned records, but no flag_match metadata is present. The API may need the latest backend update.", "error");
      } else {
        showToast(`${data.record_count || 0} account(s) selected.`, "info");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const workflowPayload = (subject, investigationId, report, risk) => ({
    provider: "dociify",
    investigation_id: investigationId,
    report_url: report.view || report.download || "",
    download_url: report.download || "",
    risk_score: risk.risk_score || null,
    risk_level: risk.risk_level || null,
    subject,
    completed_at: new Date().toISOString(),
  });

  const runEtl = async (processingRule) => {
    if (!writeBack) {
      showToast("Enable HubSpot write-back before running ETL.", "error");
      return;
    }

    setBusy(true);
    let runId = 0;
    let selected = 0;
    let successful = 0;
    let failed = 0;
    const outcomes = [];

    try {
      const selectedFlagTarget = flagTargetFor(processingRule);
      const started = await hubSpotRequest({
        action: "run_log_start",
        trigger_type: "portal",
        processing_rule: processingRule,
        requested_limit: Number(runConfig.limit),
        details: {
          query: effectiveQuery(runConfig.query, processingRule),
          enabled_rules: enabledRulesFor(processingRule),
          flag_target: selectedFlagTarget,
        },
      });
      runId = started.run_id;

      setProgress({ title: "Loading HubSpot accounts", meta: "Applying account rules..." });
      const pull = await hubSpotRequest({
        action: "run",
        ...apiConnection(),
        scope: "all",
        query: effectiveQuery(runConfig.query, processingRule),
        limit: runConfig.limit,
        processing_rule: processingRule,
        processed_property: properties.property_investigation_id,
        flag_property: selectedFlagTarget.property,
        flag_trigger_value: selectedFlagTarget.trigger_value,
        flag_complete_value: selectedFlagTarget.complete_value,
      });
      const records = pull.records || [];
      selected = records.length;

      if (!records.length) {
        await hubSpotRequest({
          action: "run_log_update",
          run_id: runId,
          status: "no_records",
          discovered_count: 0,
          success_count: 0,
          failed_count: 0,
          skipped_count: 0,
        });
        setProgress({ state: "complete", title: "No accounts to process", meta: "No HubSpot contacts matched this rule." });
        await loadLogs();
        return;
      }

      for (let index = 0; index < records.length; index += 1) {
        const subject = records[index];
        let investigationId = "";
        let risk = {};
        let report = {};

        try {
          for (const step of WORKFLOW_STEPS) {
            setProgress({
              title: `Account ${index + 1} of ${records.length}: ${step.label}`,
              meta: `${subject.name || subject.email} | ${successful} successful, ${failed} failed`,
            });
            // eslint-disable-next-line no-await-in-loop
            const result = await runWorkflowStep(step, investigationId, subject);
            if (step.key === "initialize") investigationId = result.investigation_id;
            if (step.key === "risk") risk = result.risk || result || {};
            if (step.key === "report") report = result;
          }

          const payload = workflowPayload(subject, investigationId, report, risk);
          // eslint-disable-next-line no-await-in-loop
          await hubSpotRequest({
            action: "writeback",
            ...apiConnection(),
            ...properties,
            flag_property: processingRule === "property_flag" ? selectedFlagTarget.property : "",
            flag_complete_value: processingRule === "property_flag" ? selectedFlagTarget.complete_value : "",
            subject,
            workflow_result: {
              investigation_id: investigationId,
              subject,
              report: {
                file: report.file || "",
                view_url: report.view || report.download || "",
                download_url: report.download || "",
              },
              risk,
              payload,
            },
          });
          successful += 1;
          outcomes.push({ name: subject.name, status: "success", investigation_id: investigationId });
        } catch (error) {
          failed += 1;
          outcomes.push({ name: subject.name || subject.email, status: "failed", error: error.message });
        }

        // eslint-disable-next-line no-await-in-loop
        await hubSpotRequest({
          action: "run_log_update",
          run_id: runId,
          status: "running",
          discovered_count: selected,
          success_count: successful,
          failed_count: failed,
          skipped_count: 0,
          details: { outcomes },
        });
      }

      const status = failed ? (successful ? "completed_with_errors" : "failed") : "completed";
      await hubSpotRequest({
        action: "run_log_update",
        run_id: runId,
        status,
        discovered_count: selected,
        success_count: successful,
        failed_count: failed,
        skipped_count: 0,
        details: { outcomes },
      });
      setProgress({
        state: failed ? "error" : "complete",
        title: failed ? "Run completed with errors" : "Run completed",
        meta: `${successful} successful, ${failed} failed, ${selected} selected`,
      });
      setTechnicalOutput(JSON.stringify({ run_id: runId, selected, successful, failed, outcomes }, null, 2));
      await loadLogs();
    } catch (error) {
      setProgress({ state: "error", title: "Run failed", meta: error.message });
      showToast(error.message, "error");
      if (runId) {
        await hubSpotRequest({
          action: "run_log_update",
          run_id: runId,
          status: "failed",
          discovered_count: selected,
          success_count: successful,
          failed_count: Math.max(failed, 1),
          error_message: error.message,
          details: { outcomes },
        }).catch(() => {});
      }
      await loadLogs();
    } finally {
      setBusy(false);
    }
  };

  const activeWorkflowStep = WORKFLOW_STEPS.find((step) => step.key === activeWorkflowTab) || WORKFLOW_STEPS[0];
  const activeWorkflowDefinition = workflowParameterDefinitions[activeWorkflowStep.key] || { summary: "", fields: [] };
  const activeWorkflowValues = workflowParameters[activeWorkflowStep.key] || {};

  const renderWorkflowField = (stepKey, field) => {
    const value = activeWorkflowValues[field.key] ?? field.defaultValue;
    if (field.type === "checkbox") {
      return (
        <label className="workflow-toggle" key={field.key}>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(event) => updateWorkflowParameter(stepKey, field.key, event.target.checked)}
          />
          <span>{field.label}</span>
        </label>
      );
    }

    if (field.type === "select") {
      return (
        <label className="field-label" key={field.key}>
          {field.label}
          <select
            value={value}
            onChange={(event) => updateWorkflowParameter(stepKey, field.key, event.target.value)}
          >
            {(field.options || []).map((option) => (
              <option value={option} key={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      );
    }

    return (
      <label className="field-label" key={field.key}>
        {field.label}
        <input
          type={field.type === "number" ? "number" : "text"}
          min={field.min}
          max={field.max}
          value={value}
          onChange={(event) => updateWorkflowParameter(
            stepKey,
            field.key,
            field.type === "number" ? Number(event.target.value) : event.target.value
          )}
        />
      </label>
    );
  };

  return (
    <main className="page-container deepdd-page deepdd-settings-page">
      <header className="deepdd-header">
        <div>
          <span className="deepdd-eyebrow">3D Intelligence</span>
          <h1>Due Diligence Integration Settings</h1>
          <p>Connect HubSpot, configure account rules, schedule runs, and review operational status.</p>
        </div>
        <button className="deepdd-button secondary" onClick={() => navigate("/DeepDD")}>
          <ArrowBackIcon fontSize="small" /> Investigation
        </button>
      </header>

      {toast && (
        <div className={`deepdd-toast ${toast.type}`} role="status" aria-live="polite">
          <CheckCircleIcon fontSize="small" />
          <span>{toast.message}</span>
          <button type="button" aria-label="Dismiss notification" onClick={() => setToast(null)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      )}

      <nav className="settings-tabbar" aria-label="DeepDD settings sections">
        {settingsTabs.map((tab) => (
          <button
            type="button"
            key={tab.key}
            className={activeSettingsTab === tab.key ? "active" : ""}
            onClick={() => setActiveSettingsTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeSettingsTab === "integration" && (
        <>
      <section className="deepdd-card connector-switcher-card">
        <div className="card-heading">
          <div>
            <h2>Connector Profiles</h2>
            <p>Save and switch between multiple HubSpot connections. The selected profile is used for testing, scheduling, previews, and ETL runs.</p>
          </div>
          <button className="deepdd-button primary" type="button" onClick={addConnector} disabled={busy}>
            Add Connector
          </button>
        </div>
        <div className="connector-switcher-body">
          <label className="field-label connector-select">
            Active connector
            <select value={activeConnectorId} onChange={(event) => selectConnector(event.target.value)} disabled={busy}>
              {connectorProfiles.map((profile) => (
                <option value={profile.id} key={profile.id}>
                  {profile.name || profile.connection.label || "HubSpot CRM"}
                </option>
              ))}
            </select>
          </label>
          <div className="connector-profile-list">
            {connectorProfiles.map((profile) => (
              <button
                type="button"
                key={profile.id}
                className={`connector-pill ${profile.id === activeConnectorId ? "active" : ""}`}
                onClick={() => selectConnector(profile.id)}
                disabled={busy}
              >
                <span>
                  {profile.name || profile.connection.label || "HubSpot CRM"}
                  {profile.id === activeConnectorId && <TaskAltIcon fontSize="small" />}
                </span>
                <small>{profile.connection.has_token || profile.connection.access_token ? "Token saved" : "Token needed"}</small>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="settings-layout">
        <section className="deepdd-card settings-column">
          <div className="card-heading"><div><h2>HubSpot Connection</h2><p>Use a private app token with CRM object permissions.</p></div><span className="active-connector-badge">{connection.label || "HubSpot CRM"}</span></div>
          <div className="settings-form">
            <label className="field-label">Connection label<input value={connection.label} onChange={(e) => setConnection({ ...connection, label: e.target.value })} /></label>
            <label className="field-label">HubSpot API base URL<input value={connection.base_url} onChange={(e) => setConnection({ ...connection, base_url: e.target.value })} /></label>
            <label className="field-label">Private app access token<input type="password" value={connection.access_token} placeholder={connection.has_token ? "Saved token available" : "pat-na1-..."} onChange={(e) => setConnection({ ...connection, access_token: e.target.value })} /></label>

            <div className="settings-block">
              <h3>Task Schedule</h3>
              <div className="field-grid">
                <label className="field-label">Status<select value={schedule.enabled ? "active" : "disabled"} onChange={(e) => setSchedule({ ...schedule, enabled: e.target.value === "active" })}><option value="active">Active</option><option value="disabled">Disabled</option></select></label>
                <label className="field-label">Frequency<select value={schedule.frequency} onChange={(e) => setSchedule({ ...schedule, frequency: e.target.value })}><option value="immediate">Run immediately</option><option value="every_5_minutes">Every 5 minutes</option><option value="every_30_minutes">Every 30 minutes</option><option value="hourly">Hourly</option><option value="daily">Daily</option><option value="weekly">Weekly</option></select></label>
                <label className="field-label">Account rule<select value={schedule.processing_rule} onChange={(e) => setSchedule({ ...schedule, processing_rule: e.target.value })}><option value="new_accounts">New accounts only</option><option value="property_flag">Flagged HubSpot contacts</option><option value="force_all">All accounts (forced)</option></select></label>
                <label className="field-label">Search query<input value={schedule.query} onChange={(e) => setSchedule({ ...schedule, query: e.target.value })} placeholder="Optional search" /></label>
                <label className="field-label">Max records<input type="number" min="1" max="100" value={schedule.limit} onChange={(e) => setSchedule({ ...schedule, limit: e.target.value })} /></label>
              </div>
            </div>

            <div className="button-row">
              <button className="deepdd-button primary" onClick={saveConnection} disabled={busy}><SaveIcon fontSize="small" /> Save</button>
              <button className="deepdd-button secondary" onClick={testConnection} disabled={busy}><CheckCircleIcon fontSize="small" /> Test Token</button>
              <button className="deepdd-button success" onClick={saveSchedule} disabled={busy}>Save Schedule</button>
              <button className="icon-action danger" title="Remove HubSpot" onClick={removeConnection}><DeleteOutlineIcon fontSize="small" /></button>
            </div>

            <div className="settings-block api-target-compact">
              <div className="block-heading-row">
                <div>
                  <h3>DeepDD API Target</h3>
                  <p>Set the shared backend location used by the Investigation and Settings screens.</p>
                </div>
                <span className={`environment-badge ${apiTargets.environment}`}>{apiTargets.environment === "production" ? "Production" : "Test"}</span>
              </div>
              <div className="field-grid">
                <label className="field-label">Active environment<select value={apiTargets.environment} onChange={(e) => setApiTargets({ ...apiTargets, environment: e.target.value })}><option value="test">Test</option><option value="production">Production</option></select></label>
                <label className="field-label">Test API location<input value={apiTargets.test_url} onChange={(e) => setApiTargets({ ...apiTargets, test_url: e.target.value })} placeholder="https://dev2.4pay.ca/portal/www/3D/business" /></label>
                <label className="field-label">Production API location<input value={apiTargets.production_url} onChange={(e) => setApiTargets({ ...apiTargets, production_url: e.target.value })} placeholder="https://dev2.4pay.ca/dociify3" /></label>
              </div>
              <div className="api-target-actions">
                <p>Changes apply immediately to all DeepDD API calls in this browser. No Portal rebuild is required.</p>
                <button className="deepdd-button primary" type="button" onClick={saveApiTargets}><SaveIcon fontSize="small" /> Save API Target</button>
              </div>
            </div>
          </div>
        </section>

        <section className="deepdd-card settings-column">
          <div className="card-heading"><div><h2>HubSpot ETL</h2><p>Preview accounts or run the complete due diligence workflow.</p></div></div>
          <div className="settings-form">
            <div className="field-grid">
              <label className="field-label">Account rule<select value={runConfig.processing_rule} onChange={(e) => setRunConfig({ ...runConfig, processing_rule: e.target.value })}><option value="new_accounts">New accounts only</option><option value="property_flag">Flagged HubSpot contacts</option><option value="force_all">All accounts (forced)</option></select></label>
              <label className="field-label">Max records<input type="number" min="1" max="100" value={runConfig.limit} onChange={(e) => setRunConfig({ ...runConfig, limit: e.target.value })} /></label>
            </div>
            <label className="field-label">Optional search<input value={runConfig.query} onChange={(e) => setRunConfig({ ...runConfig, query: e.target.value })} placeholder="Email, company, domain, or contact name" /></label>
            <button className="deepdd-button secondary preview-button" onClick={previewAccounts} disabled={busy}>Preview Accounts</button>

            <div className="settings-block etl-query-rules">
              <div className="block-heading-row">
                <div>
                  <h3>ETL query rules</h3>
                  <p>Create reusable query fragments and assign them to an account rule. Enabled rules are combined with Optional search for preview and ETL runs.</p>
                </div>
              </div>
              <div className="field-grid rule-builder-grid">
                <label className="field-label">
                  Rule name
                  <input value={newEtlRule.name} onChange={(e) => setNewEtlRule({ ...newEtlRule, name: e.target.value })} placeholder="High intent contacts" />
                </label>
                <label className="field-label">
                  Rule type
                  <select value={newEtlRule.rule_type} onChange={(e) => setNewEtlRule({ ...newEtlRule, rule_type: e.target.value })}>
                    <option value="query">Text query</option>
                    <option value="property_flag">HubSpot property flag</option>
                  </select>
                </label>
                <label className="field-label">
                  Assign to account rule
                  <select value={newEtlRule.account_rule} onChange={(e) => setNewEtlRule({ ...newEtlRule, account_rule: e.target.value })}>
                    <option value="new_accounts">New accounts only</option>
                    <option value="property_flag">Flagged HubSpot contacts</option>
                    <option value="force_all">All accounts (forced)</option>
                  </select>
                </label>
                {newEtlRule.rule_type === "query" ? (
                  <label className="field-label rule-query-field">
                    Query
                    <input value={newEtlRule.query} onChange={(e) => setNewEtlRule({ ...newEtlRule, query: e.target.value })} placeholder="Company, domain, email, lifecycle stage, or contact text" />
                  </label>
                ) : (
                  <>
                    <label className="field-label">
                      HubSpot property
                      <input value={newEtlRule.flag_property} onChange={(e) => setNewEtlRule({ ...newEtlRule, flag_property: e.target.value })} placeholder="requires_4pay_enrichment" />
                    </label>
                    <label className="field-label">
                      Trigger value
                      <input value={newEtlRule.flag_trigger_value} onChange={(e) => setNewEtlRule({ ...newEtlRule, flag_trigger_value: e.target.value })} placeholder="Yes" />
                    </label>
                    <label className="field-label">
                      Completed value
                      <input value={newEtlRule.flag_complete_value} onChange={(e) => setNewEtlRule({ ...newEtlRule, flag_complete_value: e.target.value })} placeholder="Done" />
                    </label>
                  </>
                )}
                <label className="rule-enabled-control">
                  <input type="checkbox" checked={newEtlRule.enabled} onChange={(e) => setNewEtlRule({ ...newEtlRule, enabled: e.target.checked })} />
                  Enabled
                </label>
              </div>
              <div className="rule-form-actions">
                <button className="deepdd-button secondary add-rule-button" type="button" onClick={addEtlRule} disabled={busy}>
                  {editingRuleId ? <SaveIcon fontSize="small" /> : <AddCircleOutlineIcon fontSize="small" />}
                  {editingRuleId ? "Save Rule" : "Add Rule"}
                </button>
                {editingRuleId && (
                  <button className="deepdd-button quiet" type="button" onClick={cancelRuleEdit} disabled={busy}>
                    Cancel
                  </button>
                )}
              </div>

              <div className="etl-rule-table-wrap">
                <table className="etl-rule-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Rule</th>
                      <th>Type</th>
                      <th>Assigned account rule</th>
                      <th>Criteria</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {etlRules.map((rule) => (
                      <tr key={rule.id} className={!rule.enabled ? "disabled-rule" : ""}>
                        <td>
                          <label className="compact-toggle">
                            <input type="checkbox" checked={rule.enabled} onChange={(e) => updateEtlRule(rule.id, { enabled: e.target.checked })} />
                            <span>{rule.enabled ? "Enabled" : "Disabled"}</span>
                          </label>
                        </td>
                        <td>{rule.name}</td>
                        <td>{rule.rule_type === "property_flag" ? "Property flag" : "Text query"}</td>
                        <td>{ruleLabels[rule.account_rule] || rule.account_rule}</td>
                        <td className="query-cell">
                          {rule.rule_type === "property_flag"
                            ? `${rule.flag_property} = ${rule.flag_trigger_value}; complete as ${rule.flag_complete_value || "-"}`
                            : rule.query}
                        </td>
                        <td className="rule-action-cell">
                          <button className="icon-action" type="button" title="Edit rule" onClick={() => editEtlRule(rule)}>
                            <EditIcon fontSize="small" />
                          </button>
                          <button className="icon-action danger" type="button" title="Remove rule" onClick={() => removeEtlRule(rule.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!etlRules.length && (
                      <tr>
                        <td colSpan="6">No ETL rules have been created yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="effective-query-preview">
                <strong>Current effective rules</strong>
                <span>Query: {effectiveQuery(runConfig.query, runConfig.processing_rule) || "No text query filters applied"}</span>
                <span>
                  Flag: {runConfig.processing_rule === "property_flag"
                    ? `${flagTargetFor(runConfig.processing_rule).property} = ${flagTargetFor(runConfig.processing_rule).trigger_value}; complete as ${flagTargetFor(runConfig.processing_rule).complete_value}`
                    : "Only used when Account rule is Flagged HubSpot contacts"}
                </span>
              </div>
            </div>

            <label className="writeback-control">
              <input type="checkbox" checked={writeBack} onChange={(e) => setWriteBack(e.target.checked)} />
              <span><strong>Write results back to HubSpot</strong><small>Required to mark completed contacts and prevent duplicate processing.</small></span>
            </label>

            <details className="property-mapping">
              <summary>HubSpot property mapping</summary>
              <div className="field-grid">
                {Object.entries(properties).map(([key, value]) => (
                  <label className="field-label" key={key}>
                    {key.replace("property_", "").replaceAll("_", " ")}
                    <input value={value} onChange={(e) => setProperties({ ...properties, [key]: e.target.value })} />
                  </label>
                ))}
              </div>
            </details>

            <div className="etl-rule-note">New Accounts skips contacts with a Dociify ID. Flagged contacts selects records where the configured HubSpot property equals the trigger value and marks it complete after write-back. Force Run All deliberately reprocesses every matching contact.</div>
            <div className="etl-actions portal-etl-actions">
              <button className="deepdd-button success" disabled={busy || !writeBack} onClick={() => runEtl("new_accounts")}><PlayArrowIcon fontSize="small" /> Run New Accounts</button>
              <button className="deepdd-button primary" disabled={busy || !writeBack} onClick={() => runEtl("property_flag")}><PlayArrowIcon fontSize="small" /> Run Flagged</button>
              <button className="deepdd-button primary" disabled={busy || !writeBack} onClick={() => runEtl("force_all")}><PlayArrowIcon fontSize="small" /> Force Run All</button>
            </div>

            {progress && (
              <div className={`portal-progress ${progress.state || ""}`}>
                {!progress.state && <span className="portal-spinner" />}
                <span><strong>{progress.title}</strong><small>{progress.meta}</small></span>
              </div>
            )}

            <details className="technical-output">
              <summary>Technical output</summary>
              <pre>{technicalOutput}</pre>
            </details>
          </div>
        </section>
      </section>
        </>
      )}

      {activeSettingsTab === "workflow" && (
        <section className="deepdd-card workflow-settings-card">
          <div className="card-heading">
            <div>
              <h2>Workflow Parameters</h2>
              <p>Configure how each DeepDD workflow block behaves for the active connector.</p>
            </div>
            <div className="workflow-actions">
              <button className="deepdd-button secondary" type="button" onClick={resetWorkflowParameters} disabled={busy}>
                Reset Defaults
              </button>
              <button className="deepdd-button primary" type="button" onClick={saveWorkflowParameters} disabled={busy}>
                <SaveIcon fontSize="small" /> Save Workflow
              </button>
            </div>
          </div>
          <div className="workflow-settings-body">
            <aside className="workflow-step-tabs" aria-label="Workflow blocks">
              {WORKFLOW_STEPS.map((step) => (
                <button
                  type="button"
                  key={step.key}
                  className={activeWorkflowTab === step.key ? "active" : ""}
                  onClick={() => setActiveWorkflowTab(step.key)}
                >
                  <span>{step.number}</span>
                  <strong>{step.label}</strong>
                </button>
              ))}
            </aside>
            <section className="workflow-parameter-panel">
              <div className="workflow-panel-heading">
                <span>{activeWorkflowStep.number}</span>
                <div>
                  <h3>{activeWorkflowStep.label}</h3>
                  <p>{activeWorkflowDefinition.summary}</p>
                </div>
              </div>
              <div className="workflow-parameter-grid">
                {activeWorkflowDefinition.fields.map((field) => renderWorkflowField(activeWorkflowStep.key, field))}
              </div>
              <div className="workflow-preview">
                <strong>Saved payload key</strong>
                <code>{activeWorkflowStep.key}</code>
              </div>
            </section>
          </div>
        </section>
      )}

      {activeSettingsTab === "automation" && (
      <section className="deepdd-card portal-log-card">
        <div className="card-heading">
          <div><h2>Automation Schedule</h2><p>Current HubSpot processing cadence and execution status.</p></div>
        </div>
        <div className="schedule-insight-layout">
          <div className="history-table-wrap schedule-table-wrap">
            <table>
              <thead><tr><th>Integration</th><th>Frequency</th><th>Account rule</th><th>Status</th><th>Last run</th><th>Last changed</th></tr></thead>
              <tbody>
                <tr>
                  <td><strong>{connection.label || "HubSpot CRM"}</strong></td>
                  <td>{frequencyLabels[schedule.frequency] || schedule.frequency}</td>
                  <td>{ruleLabels[schedule.processing_rule] || schedule.processing_rule}</td>
                  <td><span className={`schedule-status ${schedule.enabled ? "active" : "disabled"}`}>{schedule.enabled ? "Active" : "Disabled"}</span></td>
                  <td>{formatDate(lastScheduledRun?.ran_at || logs.find((run) => run.trigger_type === "scheduled")?.started_at)}</td>
                  <td>{formatDate(schedule.updated_at)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <aside className="run-activity-panel">
            <div className="run-activity-header">
              <div>
                <h3>Run Activity</h3>
                <p>Records selected, successful, and failed by run date.</p>
              </div>
              <div className="range-toggle" aria-label="Run activity range">
                {RUN_CHART_RANGES.map((range) => (
                  <button
                    type="button"
                    key={range}
                    className={runChartRange === range ? "active" : ""}
                    onClick={() => setRunChartRange(range)}
                  >
                    {range === 1 ? "1 day" : `${range} days`}
                  </button>
                ))}
              </div>
            </div>
            <div className="run-kpi-row">
              <span><strong>{chartTotals.runs}</strong><small>Runs</small></span>
              <span><strong>{chartTotals.selected}</strong><small>Selected</small></span>
              <span className="success"><strong>{chartTotals.successful}</strong><small>Successful</small></span>
              <span className="failed"><strong>{chartTotals.failed}</strong><small>Failed</small></span>
            </div>
            <div className="run-activity-chart">
              {chartBuckets.map((bucket) => {
                const selectedHeight = Math.max(4, (bucket.selected / maxChartValue) * 100);
                const successHeight = Math.max(bucket.successful ? 4 : 0, (bucket.successful / maxChartValue) * 100);
                const failedHeight = Math.max(bucket.failed ? 4 : 0, (bucket.failed / maxChartValue) * 100);
                return (
                  <div className="run-chart-day" key={bucket.key} title={`${bucket.label}: ${bucket.selected} selected, ${bucket.successful} successful, ${bucket.failed} failed`}>
                    <div className="run-chart-bars">
                      <span className="selected-bar" style={{ height: `${selectedHeight}%` }} />
                      <span className="success-bar" style={{ height: `${successHeight}%` }} />
                      <span className="failed-bar" style={{ height: `${failedHeight}%` }} />
                    </div>
                    <small>{bucket.label}</small>
                  </div>
                );
              })}
            </div>
            {!chartTotals.runs && <div className="empty-chart-note">No runs found in this range.</div>}
          </aside>
        </div>
      </section>
      )}

      {activeSettingsTab === "notifications" && (
        <section className="deepdd-card notification-settings-card">
          <div className="card-heading">
            <div>
              <h2>Notifications and Daily Limits</h2>
              <p>Manage threshold email delivery and the maximum number of profiles started each day.</p>
            </div>
            <span className="active-connector-badge">{connection.label || "HubSpot CRM"}</span>
          </div>
          <div className="settings-form">
            <div className="field-grid">
              <label className="field-label">Notifications<select value={schedule.daily_notification_enabled ? "enabled" : "disabled"} onChange={(e) => setSchedule({ ...schedule, daily_notification_enabled: e.target.value === "enabled" })}><option value="enabled">Enabled</option><option value="disabled">Disabled</option></select></label>
              <label className="field-label">Notify at profiles<input type="number" min="1" max="1000" value={schedule.daily_notification_threshold} onChange={(e) => setSchedule({ ...schedule, daily_notification_threshold: e.target.value })} /></label>
              <label className="field-label">Stop at profiles<input type="number" min="1" max="1000" value={schedule.daily_limit} onChange={(e) => setSchedule({ ...schedule, daily_limit: e.target.value })} /></label>
              <label className="field-label">Daily reset timezone<input value={schedule.daily_timezone} onChange={(e) => setSchedule({ ...schedule, daily_timezone: e.target.value })} placeholder="America/Toronto" /></label>
            </div>
            <label className="field-label">Notification recipients<textarea rows="3" value={schedule.daily_notification_recipients} onChange={(e) => setSchedule({ ...schedule, daily_notification_recipients: e.target.value })} placeholder="name@example.com, team@example.com" /></label>
            <div className="button-row">
              <button className="deepdd-button primary" type="button" onClick={saveNotificationSettings} disabled={busy}><SaveIcon fontSize="small" /> Save Notification Settings</button>
              <button className="deepdd-button secondary" type="button" onClick={sendTestNotification} disabled={busy}><EmailOutlinedIcon fontSize="small" /> Send Test Email</button>
            </div>
            <details className="technical-output">
              <summary>Technical output</summary>
              <pre>{technicalOutput}</pre>
            </details>
          </div>
        </section>
      )}

      {activeSettingsTab === "logs" && (
      <section className="deepdd-card portal-log-card">
        <div className="card-heading">
          <div><h2>HubSpot Run Log</h2><p>Processing outcomes from portal and scheduled runs.</p></div>
          <button className="icon-action" title="Refresh logs" onClick={loadLogs}><RefreshIcon fontSize="small" /></button>
        </div>
        <div className="history-table-wrap">
          <table>
            <thead><tr><th>Started</th><th>Trigger</th><th>Rule</th><th>Status</th><th>Selected</th><th>Successful</th><th>Failed</th><th>Reason</th><th>Completed</th></tr></thead>
            <tbody>
              {pagedLogs.map((run) => (
                <tr key={run.id}>
                  <td>{formatDate(run.started_at)}</td>
                  <td>{run.trigger_type}</td>
                  <td>{ruleLabels[run.processing_rule] || run.processing_rule}</td>
                  <td><span className={`run-log-status ${run.status}`}>{run.status.replaceAll("_", " ")}</span></td>
                  <td>{run.discovered_count}</td>
                  <td className="success-count">{run.success_count}</td>
                  <td className="failure-count">{run.failed_count}</td>
                  <td className="run-reason-cell">{runFailureReason(run)}</td>
                  <td>{formatDate(run.completed_at)}</td>
                </tr>
              ))}
              {!logs.length && <tr><td colSpan="9">No HubSpot runs have been logged yet.</td></tr>}
            </tbody>
          </table>
        </div>
        {!!logs.length && (
          <div className="run-log-pagination">
            <span>
              Showing {logStartIndex + 1}-{Math.min(logStartIndex + RUN_LOG_PAGE_SIZE, logs.length)} of {logs.length}
            </span>
            <div className="pagination-controls">
              <button
                className="deepdd-button secondary"
                type="button"
                onClick={() => setLogPage((page) => Math.max(1, page - 1))}
                disabled={safeLogPage <= 1}
              >
                Previous
              </button>
              <span>Page {safeLogPage} of {totalLogPages}</span>
              <button
                className="deepdd-button secondary"
                type="button"
                onClick={() => setLogPage((page) => Math.min(totalLogPages, page + 1))}
                disabled={safeLogPage >= totalLogPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>
      )}
    </main>
  );
}

export default DeepDDSettings;
